import { Component, ElementRef, OnDestroy, OnInit } from "@angular/core";
import {
  Router,
  NavigationStart,
  NavigationCancel,
  NavigationEnd,
} from "@angular/router";
import {
  Location,
  LocationStrategy,
  PathLocationStrategy,
} from "@angular/common";
import {
  delay,
  filter,
  finalize,
  mergeMap,
  retryWhen,
  take,
  tap,
} from "rxjs/operators";
import { AppSettingService } from "src/services/app-setting/app-setting.service";
import { AuthService } from "src/services/auth/auth.service";
import { User } from "src/models/user.model";
import { UserService } from "src/services/user/user.service";
import { PrefectureService } from "src/services/prefecture/prefecture.service";
import { Prefecture } from "src/models/prefecture";
import { CommmuneService } from "src/services/commune/commmune.service";
import { RegionService } from "src/services/region/region.service";
import { CommuneModel } from "src/models/commune.model";
import { Region } from "src/models/region";
import { CountriesService } from "src/services/countries/countries.service";
import { CountriesModel } from "src/models/countries.model";
import { CategoriesService } from "src/services/categories/categories.service";
import { CategoriesModel } from "src/models/categories.model";
import { StaffService } from "src/services/staff/staff.service";
import { Staff } from "src/models/staff.model";
import { FaqService } from "src/services/faq/faq.service";
import { FaqModel } from "src/models/faq.model";

// declare let $: any;

import * as $ from "jquery";
import { NetworkStatusService } from "src/services/network-status/network-status.service";
import { of, Subscription, throwError } from "rxjs";
import Swal from "sweetalert2";
import { Meta } from "@angular/platform-browser";
import { AppSettingModel } from "src/models/app-setting.model";
import { CompanyTypeModel } from "src/models/company_type.model";
import { CompanyTypService } from "src/services/company_type/company-typ.service";
@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  providers: [
    Location,
    {
      provide: LocationStrategy,
      useClass: PathLocationStrategy,
    },
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  location: any;
  routerSubscription: any;
  errorLoading: boolean;
  status: number;
  isOnline: boolean = true; // Initialisez à true, supposant une connexion au démarrage
  private networkStatusSubscription: Subscription;

  loading = true;
  minLoadingTime = 2000; // Temps minimum d'affichage du loader (2 secondes)
  progress = 0;

  constructor(
    private router: Router,
    public authService: AuthService,
    private userService: UserService,
    public appSettingService: AppSettingService,
    private _elementRef: ElementRef,
    private prefectureService: PrefectureService,
    private communeService: CommmuneService,
    private regionService: RegionService,
    private countryService: CountriesService,
    private categoryService: CategoriesService,
    private staffService: StaffService,
    private faqService: FaqService,
    private meta: Meta,
    private networkStatusService: NetworkStatusService,
    private companyTypeService: CompanyTypService
  ) {}

  private toastShown = false; // Pour éviter d'afficher plusieurs toasts

  ngOnInit() {
    // $.getScript("../assets/js/security.js");
    this._elementRef.nativeElement.removeAttribute("ng-version");
    // Vérifie automatiquement l'expiration du token
    this.authService.checkAndRemoveExpiredToken();

    if (this.authService.isTokenExpired()) {
      // Redirigez vers la page de login
      this.router.navigate(["/"]);
    }

    this.loadAppSettings();
    this.startProgressAnimation();

    // Écoute les changements de statut de connexion
    this.networkStatusSubscription =
      this.networkStatusService.isOnline.subscribe((online) => {
        this.isOnline = online;
        if (!online && !this.toastShown) {
          this.showOfflineToast();
          this.toastShown = true;
          console.log("Vous êtes hors ligne !");
        } else if (online && this.toastShown) {
          this.showOnlineToast();
          this.toastShown = false;
          console.log("Vous êtes de nouveau en ligne !");
        }
      });
    this.setMetaTags();
    this.recallJsFunctions();
  }

  private setMetaTags() {
    const siteUrl = "https://bstpgn.com";
    const pageTitle = "BSTP Guinée - Accueil";
    const pageDescription =
      "A propos de nous - Description détaillée pour les réseaux sociaux.";
    const imageUrl = `${siteUrl}/assets/img/upload/slides/img-1.webp`; // Chemin vers votre image (1200x630px recommandé)

    // Balises standards (SEO)
    this.meta.updateTag({ name: "description", content: pageDescription });
    this.meta.updateTag({
      name: "keywords",
      content: "BSTP Guinée, BSTP, bstpgn",
    });
    this.meta.updateTag({ name: "author", content: "BSTP Guinée" });

    // Open Graph (Facebook, WhatsApp, LinkedIn, etc.)
    this.meta.updateTag({ property: "og:type", content: "website" });
    this.meta.updateTag({ property: "og:url", content: siteUrl });
    this.meta.updateTag({ property: "og:title", content: pageTitle });
    this.meta.updateTag({
      property: "og:description",
      content: pageDescription,
    });
    this.meta.updateTag({ property: "og:image", content: imageUrl });
    this.meta.updateTag({ property: "og:image:width", content: "1200" });
    this.meta.updateTag({ property: "og:image:height", content: "630" });

    // Twitter Card
    this.meta.updateTag({
      name: "twitter:card",
      content: "summary_large_image",
    });
    this.meta.updateTag({ name: "twitter:title", content: pageTitle });
    this.meta.updateTag({
      name: "twitter:description",
      content: pageDescription,
    });
    this.meta.updateTag({ name: "twitter:image", content: imageUrl });
  }

  private loadAppSettings(): void {
    const startTime = Date.now();

    this.appSettingService
      .get()
      .pipe(
        retryWhen((errors) =>
          errors.pipe(
            mergeMap((error) => {
              // Si c'est une erreur serveur (500+), on réessaie après un délai
              if (error.status >= 500) {
                console.log("Erreur serveur, nouvelle tentative...");
                return of(error).pipe(delay(500));
              }
              // Sinon (404, 400, etc.), on propage immédiatement l'erreur
              return throwError(() => error);
            }),
            take(3) // Max 3 tentatives pour les erreurs 5xx
          )
        )
      )
      .subscribe({
        next: (data: Array<AppSettingModel>) => {
          const elapsed = Date.now() - startTime;
          const remainingTime = Math.max(0, this.minLoadingTime - elapsed);

          setTimeout(() => {
            this.loading = false;
          }, remainingTime);
          this.appSettingService.update(data[0]);
          this.prefectureService
            .list()
            .pipe(
              retryWhen((errors) =>
                errors.pipe(
                  mergeMap((error) => {
                    // Si c'est une erreur serveur (500+), on réessaie après un délai
                    if (error.status >= 500) {
                      console.log("Erreur serveur, nouvelle tentative...");
                      return of(error).pipe(delay(500));
                    }
                    // Sinon (404, 400, etc.), on propage immédiatement l'erreur
                    return throwError(() => error);
                  }),
                  take(3) // Max 3 tentatives pour les erreurs 5xx
                )
              )
            )
            .subscribe((prefectures: Array<Prefecture>) =>
              this.prefectureService.update(prefectures)
            );
          this.companyTypeService
            .list()
            .pipe(
              retryWhen((errors) =>
                errors.pipe(
                  mergeMap((error) => {
                    // Si c'est une erreur serveur (500+), on réessaie après un délai
                    if (error.status >= 500) {
                      console.log("Erreur serveur, nouvelle tentative...");
                      return of(error).pipe(delay(500));
                    }
                    // Sinon (404, 400, etc.), on propage immédiatement l'erreur
                    return throwError(() => error);
                  }),
                  take(3) // Max 3 tentatives pour les erreurs 5xx
                )
              )
            )
            .subscribe((companyTypes: Array<CompanyTypeModel>) =>
              this.companyTypeService.update(companyTypes)
            );

          this.communeService
            .list()
            .pipe(
              retryWhen((errors) =>
                errors.pipe(
                  mergeMap((error) => {
                    // Si c'est une erreur serveur (500+), on réessaie après un délai
                    if (error.status >= 500) {
                      console.log("Erreur serveur, nouvelle tentative...");
                      return of(error).pipe(delay(500));
                    }
                    // Sinon (404, 400, etc.), on propage immédiatement l'erreur
                    return throwError(() => error);
                  }),
                  take(3) // Max 3 tentatives pour les erreurs 5xx
                )
              )
            )
            .subscribe((communes: Array<CommuneModel>) =>
              this.communeService.update(communes)
            );
          this.regionService
            .list()
            .pipe(
              retryWhen((errors) =>
                errors.pipe(
                  mergeMap((error) => {
                    // Si c'est une erreur serveur (500+), on réessaie après un délai
                    if (error.status >= 500) {
                      console.log("Erreur serveur, nouvelle tentative...");
                      return of(error).pipe(delay(500));
                    }
                    // Sinon (404, 400, etc.), on propage immédiatement l'erreur
                    return throwError(() => error);
                  }),
                  take(3) // Max 3 tentatives pour les erreurs 5xx
                )
              )
            )
            .subscribe((regions: Array<Region>) =>
              this.regionService.update(regions)
            );
          this.countryService
            .list()
            .pipe(
              retryWhen((errors) =>
                errors.pipe(
                  mergeMap((error) => {
                    // Si c'est une erreur serveur (500+), on réessaie après un délai
                    if (error.status >= 500) {
                      console.log("Erreur serveur, nouvelle tentative...");
                      return of(error).pipe(delay(500));
                    }
                    // Sinon (404, 400, etc.), on propage immédiatement l'erreur
                    return throwError(() => error);
                  }),
                  take(3) // Max 3 tentatives pour les erreurs 5xx
                )
              )
            )
            .subscribe((countries: Array<CountriesModel>) =>
              this.countryService.update(countries)
            );
          this.categoryService
            .list()
            .pipe(
              retryWhen((errors) =>
                errors.pipe(
                  mergeMap((error) => {
                    // Si c'est une erreur serveur (500+), on réessaie après un délai
                    if (error.status >= 500) {
                      console.log("Erreur serveur, nouvelle tentative...");
                      return of(error).pipe(delay(500));
                    }
                    // Sinon (404, 400, etc.), on propage immédiatement l'erreur
                    return throwError(() => error);
                  }),
                  take(3) // Max 3 tentatives pour les erreurs 5xx
                )
              )
            )
            .subscribe((categories: Array<CategoriesModel>) =>
              this.categoryService.update(categories)
            );

          if (this.authService.isAuthenticated()) {
            this.userService
              .currentUser()
              .subscribe((data: User) => this.authService.update(data));
          }
        },
        error: (err) => {
          console.error("Failed to load app settings", err);
          this.loading = false;
        },
      });
  }

  private startProgressAnimation(): void {
    const interval = setInterval(() => {
      this.progress += Math.random() * 15;
      if (this.progress >= 100) {
        clearInterval(interval);
      }
    }, 300);
  }

  get isBuyer(): boolean {
    return ["buyer", "buyer_collegue"].includes(
      this.authService.value?.role?.code_name
    );
  }

  get isSupplier(): boolean {
    return ["supplier", "supplier_collegue"].includes(
      this.authService.value?.role?.code_name
    );
  }

  get isOrganisme(): boolean {
    return ["cabinet", "organisme"].includes(
      this.authService.value?.role?.code_name
    );
  }

  ngOnDestroy(): void {
    if (this.networkStatusSubscription) {
      this.networkStatusSubscription.unsubscribe();
    }
  }

  recallJsFunctions() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        $(".loader").fadeIn("slow");
      }
    });
    this.routerSubscription = this.router.events
      .pipe(
        filter(
          (event) =>
            event instanceof NavigationEnd || event instanceof NavigationCancel
        )
      )
      .subscribe((event) => {
        $.getScript("../assets/js/custom.js");
        $(".loader").fadeOut("slow");
        this.location = this.router.url;
        if (!(event instanceof NavigationEnd)) {
          return;
        }
        window.scrollTo(0, 0);
      });
  }

  private showOfflineToast() {
    Swal.fire({
      toast: true,
      position: "bottom-start",
      icon: "error",
      title: "Erreur de Connexion",
      text: "Impossible de se connecter au serveur. Vérifiez votre connexion internet.",
      showConfirmButton: false,
      timer: 0, // Toast persistant jusqu'à reconnexion
      timerProgressBar: false,
      customClass: {
        popup: "offline-toast",
      },
    });
  }

  private showOnlineToast() {
    Swal.fire({
      toast: true,
      position: "bottom-start",
      icon: "success",
      title: "Connexion rétablie",
      text: "Vous êtes de nouveau connecté au serveur.",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    });
  }
}
